import React from 'react';
import { connect } from 'react-redux';
import {
  Card,
  CardBody,
  CardText,
  CardTitle,
  DashboardSection,
  Header1,
  Header2,
} from 'fiducius-ui';

import ReducedIDRProgress from '../components/reduced-idr-progress';
import { NeedAssistance } from '../../../need-assistance';
import Scheduler from '../../../meetings/containers/scheduler';
import { withPermissions } from '../../../routing';
import { isStringWithContent } from '../../../utils';
import FaqQuestion from '../../../faq/components/faq-question';

const ReducedIDRNeedAssistance = ({ permissions, needAssistanceObject }) => {
  const showSpeakToAnExpertButton = () => {
    if (permissions.clientStatus === 'FUPHB' && permissions.enterpriseEnabled) {
      return true;
    }
  };

  const ReducedIDRAssistanceObject =
    needAssistanceObject.length &&
    needAssistanceObject.find((element) => {
      return element.id === 'ReducedIDR_Assistance';
    });
  const needAssistanceText =
    ReducedIDRAssistanceObject && ReducedIDRAssistanceObject.messageContent;
  const needAssistanceAvatarLink =
    ReducedIDRAssistanceObject && ReducedIDRAssistanceObject.avatarLink;
  const blocktext =
    isStringWithContent(needAssistanceText) || isStringWithContent(needAssistanceAvatarLink)
      ? 'block'
      : 'none';

  return (
    <div className="row">
      <div className="col-12 col-lg-12">
        <ReducedIDRProgress />
        <DashboardSection>
          <div className="row">
            <div className="col-12 col-lg-9">
              <Header1>Need Assistance?</Header1>
            </div>
            {showSpeakToAnExpertButton() && (
              <>
                <div className="col-12 col-lg-3" style={{ 'text-align': 'right' }}>
                  <Scheduler buttonText="Speak to an Expert" />
                </div>
              </>
            )}
          </div>
          <NeedAssistance messageType={'ReducedIDR_Alert'} />
          <Card style={{ display: blocktext }}>
            <CardBody>
              <CardTitle>Information</CardTitle>
              <CardText>
                <NeedAssistance messageType={'ReducedIDR_Assistance'} />
              </CardText>
            </CardBody>
          </Card>
          <Card>
            <CardBody>
              <CardTitle>FAQs</CardTitle>
              <br />
              <Header2>1. Authorized Use of Consent</Header2>
              <FaqQuestion question={'Why do I need to confirm the Authorized Use of Consent?'}>
                <p>
                  Confirming the Authorized Use of Consent allows us to process your Income-Driven
                  Repayment (IDR) application and access necessary information securely.
                </p>
              </FaqQuestion>
              <FaqQuestion question={'How do I opt in?'}>
                <p>Read the provided information and select “I Agree” to proceed.</p>
              </FaqQuestion>
              <br />
              <Header2>2. Implementation - Generate IDR Application</Header2>
              <FaqQuestion
                question={'What steps do I need to follow to generate my IDR application?'}
              >
                <p>
                  You will need to:
                  <ol type="1">
                    <li>
                      Answer questions about your current federal student loan repayment plan.
                    </li>
                    <li>Provide details about your household and income.</li>
                    <li>Review and verify a 13-page application.</li>
                    <li>Sign and submit the application.</li>
                  </ol>
                </p>
              </FaqQuestion>
              <FaqQuestion question={'What information is required under the ‘Plan’ section?'}>
                <p>
                  You will need to provide details about your current repayment plan,
                  deferment/forbearance status, and start date. If unsure, check your loan servicer
                  account.
                </p>
              </FaqQuestion>
              <FaqQuestion question={'What details are needed under the ‘Household’ section?'}>
                <p>
                  You will be asked about:
                  <ul>
                    <li>Number of dependents in your household.</li>
                    <li>
                      Other individuals that live with you and are receiving over 50% financial
                      support from you.
                    </li>
                    <li>Marital status.</li>
                    <li>
                      Adjusted Gross Income (typically found on Line 11 of your latest 1040 tax
                      form).
                    </li>
                  </ul>
                </p>
              </FaqQuestion>
              <FaqQuestion question={'What is required to sign my application?'}>
                <p>
                  You will need:
                  <ul>
                    <li>First and last name</li>
                    <li>Email</li>
                    <li>Password</li>
                    <li>Social Security number</li>
                    <li>Digital signature in the designated box</li>
                  </ul>
                </p>
              </FaqQuestion>
              <br />
              <Header2>3. Upload Documents</Header2>
              <FaqQuestion question={'What documents do I need to upload?'}>
                <p>
                  You may be required to upload:
                  <ul>
                    <li>Recent Paycheck (within the past 60 days)</li>
                    <li>Tax Return (Form 1040)</li>
                    <li>W-2 Form</li>
                  </ul>
                </p>
              </FaqQuestion>
              <FaqQuestion question={'Where do I upload my documents?'}>
                <p>Upload documents in the "My Documents" section of your portal.</p>
              </FaqQuestion>
              <br />
              <Header2>4. Submit Your Repayment Application</Header2>
              <FaqQuestion question={'What should I verify before submitting my application?'}>
                <p>
                  Ensure that:
                  <ul>
                    <li>Your SSN appears on the first four pages.</li>
                    <li>Your income documents are clear and accurate.</li>
                  </ul>
                </p>
              </FaqQuestion>
              <FaqQuestion question={'How do I submit my application?'}>
                <p>
                  Check the confirmation box stating you’ve completed all steps, then select
                  “Confirm.”
                </p>
              </FaqQuestion>
              <FaqQuestion question={'How will I know my application has been submitted?'}>
                <p>
                  A notification will appear at the bottom of your screen, confirming that your IDR
                  application has been faxed to your loan servicer.
                </p>
              </FaqQuestion>
              <br />
              <Header2>5. Check on Your Repayment Application</Header2>
              <FaqQuestion question={'How do I check the status of my IDR application?'}>
                <p>
                  Log into your loan servicer account (MOHELA, AidVantage, EdFinancial, or Nelnet)
                  and check for an "In Repayment" status under Loan Details.
                </p>
              </FaqQuestion>
              <FaqQuestion question={'How often should I check for updates?'}>
                <p>Every seven days, as processing takes approximately 4-8 weeks.</p>
              </FaqQuestion>
              <FaqQuestion question={'What should I look for in my approval statement?'}>
                <p>
                  Ensure your repayment amount matches what is displayed in your portal. The
                  approval statement is typically titled “IDR RECALCULATED.”
                </p>
              </FaqQuestion>
              <FaqQuestion question={'What if my repayment amount is incorrect?'}>
                <p>
                  If the amount is higher than expected, use the “Contact Us” button on the left
                  side of your portal to request assistance.
                </p>
              </FaqQuestion>
              <br />
              <Header2>6. Completion & Renewal</Header2>
              <FaqQuestion question={'What happens after my IDR plan is set up?'}>
                <p>
                  Your new repayment plan will be in effect, and you’ll need to renew it annually,
                  starting about four months before expiration.
                </p>
              </FaqQuestion>
              <FaqQuestion question={'How do I ensure I remain enrolled in the IDR plan?'}>
                <p>
                  Complete the renewal process annually to avoid reverting to the standard repayment
                  plan.
                </p>
              </FaqQuestion>
              <FaqQuestion question={'How can I get help if I have questions?'}>
                <p>
                  Use the “Contact Us” feature in your portal to ask questions or request a call
                  with a Benefit Service representative.
                </p>
              </FaqQuestion>
            </CardBody>
          </Card>
        </DashboardSection>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  todos: Object.values(state.todos.cache),
  needAssistanceObject: Object.values(state.needAssistance.cache),
});

const mapDispatchToProps = (dispatch) => ({});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withPermissions(ReducedIDRNeedAssistance));
